import * as React from 'react';
import { useState } from 'react';
import { FormLayoutWidget } from '../../../widgets/layout/FormLayoutWidget';
import { AppModel } from '@journeyapps-platform/lib-journey-core';
import { GroupedButtons } from '../../../widgets/buttons/GroupedButtons';
import { SourceMetadata, SourceType } from '@journeyapps-platform/types-source-service';
import { useSystem } from '../../../hooks/useSystem';
import { useDialogStore } from '../../../hooks/useDialogService';
import { useOrgStore } from '../../../hooks/useOrgStore';
import { GitProvider } from '../create-app/CreateAppPage';

export interface CommonMigrateToWidgetProps<T extends SourceMetadata = SourceMetadata> {
  app: AppModel;
  close: () => any;
  createIntegration: () => any;
  provider: SourceType;
  generateSource: () => SourceMetadata;
  generateMigrateWidget: (props: {
    payload: CommonPayload<T>;
    setPayload: (p: CommonPayload<T>) => any;
  }) => JSX.Element;
}

export interface CommonPayload<T extends SourceMetadata = SourceMetadata> {
  create_repo?: boolean;
  source?: T;
}

export const CommonMigrateToWidget = <T extends SourceMetadata>(props: CommonMigrateToWidgetProps<T>): JSX.Element => {
  const { app } = props;
  const system = useSystem();
  const dialogStore = useDialogStore();
  const orgStore = useOrgStore();

  const [payload, setPayload] = useState<Partial<CommonPayload<T>>>(() => {
    return {
      source: props.generateSource() as T
    };
  });

  const getButtons = () => {
    return {
      key: 'submit',
      elements: [
        <GroupedButtons
          btns={[
            {
              label: 'Cancel',
              action: (event, loading) => {
                props.close();
              }
            },
            {
              label: 'Convert',
              action: async (event, loading) => {
                loading(true);
                const alpha = system.clientProvisioner.provisionAlphaClient();
                try {
                  await alpha.transferRepo({
                    app_id: app!.id,
                    target: payload.source!,
                    create_repo: payload.create_repo!
                  });
                  orgStore.org(app.org_id!).loadApps();
                  loading(false);
                  props.close();
                } catch (ex) {
                  loading(false);
                  dialogStore.errorDialog({
                    subTitle: `Failed to migrate to ${GitProvider[props.provider]}`,
                    errorMessage: `Oh no, something went wrong and this app couldn't change git providers.`
                  });
                }
              }
            }
          ]}
        />
      ]
    };
  };

  return (
    <FormLayoutWidget
      sections={[
        {
          key: 'Source mode',
          elements: [
            props.generateMigrateWidget({
              payload,
              setPayload
            })
          ]
        },
        getButtons()
      ]}
    />
  );
};
